/**
 * The rule-set editor. Spec 12.2's `JsonSchemaEditor` + `SimulatorPanel` + `ActivateDialog`.
 *
 * Spec 11.4, in full, because it is the reason this screen exists:
 *
 *   "The rule-set simulator is not optional. Editing a rule set changes how the game is
 *    played. The admin UI must let you run N simulated games against a draft rule set and
 *    inspect the outcome distribution — average turns to completion, entry-failure rate,
 *    unreachable squares, and whether the game terminates at all — before activation."
 *
 * Two things are worth knowing about how the gate works. The **Activate** control is
 * disabled until a simulation of the current draft has come back safe — that is the
 * affordance. And the server re-runs the simulation inside `activate` regardless, so the
 * disabled button is a convenience and the refusal is the enforcement (spec 10.8's usual
 * split: what the UI hides is never the check).
 */
import { ruleSetConfigSchema } from '@lila/shared';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '../../../components/ui/Alert';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { Dialog } from '../../../components/ui/Dialog';
import { Field } from '../../../components/ui/Field';
import { TextAreaField } from '../../../components/ui/FieldParts';
import { SkeletonList } from '../../../components/ui/Skeleton';
import {
  useActivateRuleSet,
  useAdminContent,
  useSimulateRuleSet,
  useUpsertRuleSet,
} from '../../../features/admin/api';
import { appErrorFrom, errorKeyFor } from '../../../lib/errors';

export const Route = createFileRoute('/$lang/admin/content/rulesets/$id')({ component: Page });

type Report = {
  readonly sessions: number;
  readonly completed: number;
  readonly exhausted: number;
  readonly turnsToCompletion: {
    readonly mean: number;
    readonly min: number;
    readonly median: number;
    readonly p95: number;
    readonly max: number;
  };
  readonly entryFailureRate: number;
  readonly unreachableSquares: readonly number[];
  readonly goalReachable: boolean;
  readonly terminates: boolean;
  readonly unsupported: string | null;
  readonly safeToActivate: boolean;
};

function Page(): ReactNode {
  const { t } = useTranslation(['admin', 'common']);
  const { id } = Route.useParams();
  const queryClient = useQueryClient();

  const content = useAdminContent();
  const simulate = useSimulateRuleSet();
  const activate = useActivateRuleSet();
  const save = useUpsertRuleSet();

  const [draft, setDraft] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [report, setReport] = useState<Report | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState('');

  const ruleSet = content.data?.ruleSets.find((row) => row.id === id);
  const boardId = content.data?.boards[0]?.id ?? null;

  /* Loaded once the row arrives, rather than rendering an empty editor over real config. */
  useEffect(() => {
    if (ruleSet === undefined) return;
    setDraft(JSON.stringify(ruleSet.config, null, 2));
  }, [ruleSet]);

  /**
   * Parses and validates the draft against the SHARED schema — the same one the server
   * uses (CLAUDE.md hard rule 12), so "invalid" means the same thing in both places and the
   * editor can show field errors inline (spec 12.2's "invalid (schema errors inline)").
   */
  const parseDraft = (): Record<string, unknown> | null => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(draft);
    } catch {
      setParseError(t('common:errors.VALIDATION_ERROR', { defaultValue: 'Invalid JSON' }));
      return null;
    }

    const result = ruleSetConfigSchema.safeParse(parsed);
    if (!result.success) {
      setParseError(
        result.error.issues
          .map((issue) => `${issue.path.join('.') || '(root)'}: ${issue.message}`)
          .join('\n'),
      );
      return null;
    }

    setParseError(null);
    return result.data;
  };

  const runSimulation = (): void => {
    const config = parseDraft();
    if (config === null || boardId === null) return;

    /* A changed draft invalidates the previous verdict — the gate must not go stale. */
    setReport(null);
    simulate.mutate(
      { config, boardId, sessions: 1000 },
      {
        onSuccess: (result) => {
          setReport(result);
        },
      },
    );
  };

  if (content.error !== null) {
    return <Alert tone="error">{t(errorKeyFor(content.error))}</Alert>;
  }
  if (content.isLoading) {
    return <SkeletonList rows={5} label={t('admin:ruleset.title')} />;
  }
  if (ruleSet === undefined) {
    return <Alert tone="error">{t('common:states.notFound', { defaultValue: 'Not found' })}</Alert>;
  }

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-ink">{ruleSet.name}</h1>
          <p className="text-sm text-ink-muted">
            {ruleSet.slug} · v{ruleSet.version}
          </p>
        </div>
        <Badge tone={ruleSet.isActive ? 'positive' : 'neutral'}>
          {ruleSet.isActive ? t('admin:content.active') : t('admin:content.draft')}
        </Badge>
      </header>

      <Card title={t('admin:ruleset.config')}>
        <div className="flex flex-col gap-3">
          <TextAreaField
            label={t('admin:ruleset.config')}
            /* Errors from the shared schema, one per line, with the field that produced it. */
            error={parseError ?? undefined}
            value={draft}
            rows={18}
            spellCheck={false}
            className="font-mono"
            onChange={(event) => {
              setDraft(event.target.value);
              /* Editing invalidates the verdict; activation must not ride a stale report. */
              setReport(null);
            }}
          />

          <div className="flex flex-wrap gap-2">
            <Button loading={simulate.isPending} onClick={runSimulation}>
              {simulate.isPending ? t('admin:ruleset.simulating') : t('admin:ruleset.simulate')}
            </Button>
            <Button
              variant="secondary"
              loading={save.isPending}
              onClick={() => {
                const config = parseDraft();
                if (config === null) return;
                save.mutate(
                  {
                    slug: ruleSet.slug,
                    version: ruleSet.version,
                    name: ruleSet.name,
                    config,
                  },
                  {
                    onSuccess: () => {
                      void queryClient.invalidateQueries({ queryKey: ['admin', 'content'] });
                    },
                  },
                );
              }}
            >
              {t('common:actions.save')}
            </Button>
          </div>

          {simulate.isError && <Alert tone="error">{t(errorKeyFor(simulate.error))}</Alert>}
          {save.isError && <Alert tone="error">{t(errorKeyFor(save.error))}</Alert>}
        </div>
      </Card>

      {report !== null && (
        <SimulatorPanel
          report={report}
          onActivate={() => {
            setConfirming(true);
          }}
        />
      )}

      <Dialog
        open={confirming}
        title={t('admin:ruleset.activate')}
        onClose={() => {
          setConfirming(false);
        }}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setConfirming(false);
              }}
            >
              {t('admin:confirm.cancel')}
            </Button>
            <Button
              loading={activate.isPending}
              disabled={reason.trim() === ''}
              onClick={() => {
                activate.mutate(
                  { ruleSetId: id, reason: reason.trim() },
                  {
                    onSuccess: () => {
                      void queryClient.invalidateQueries({ queryKey: ['admin', 'content'] });
                      setConfirming(false);
                    },
                  },
                );
              }}
            >
              {t('admin:confirm.confirm')}
            </Button>
          </>
        }
      >
        <Alert tone="info">{t('admin:ruleset.activateWarning')}</Alert>

        {/*
         * `RULE_SET_UNSAFE` from the server's own re-run. It can only appear if the draft
         * changed since the verdict, or if somebody reached the procedure directly — either
         * way the report is what explains it, so the panel stays on screen behind this.
         */}
        {activate.isError && appErrorFrom(activate.error)?.code === 'RULE_SET_UNSAFE' ? (
          <Alert tone="error">{t('errors:RULE_SET_UNSAFE')}</Alert>
        ) : (
          activate.isError && <Alert tone="error">{t(errorKeyFor(activate.error))}</Alert>
        )}

        <Field
          label={t('admin:ruleset.reason')}
          hint={t('admin:confirm.reasonRequired')}
          required
          maxLength={500}
          value={reason}
          onChange={(event) => {
            setReason(event.target.value);
          }}
        />
      </Dialog>
    </div>
  );
}

/** Spec 12.2's `SimulatorPanel`. Every figure spec 11.4 names, and the verdict. */
function SimulatorPanel({
  report,
  onActivate,
}: {
  readonly report: Report;
  readonly onActivate: () => void;
}): ReactNode {
  const { t } = useTranslation(['admin']);

  return (
    <Card
      title={t('admin:ruleset.results')}
      actions={
        <Badge tone={report.safeToActivate ? 'positive' : 'danger'}>
          {report.safeToActivate ? t('admin:ruleset.safe') : t('admin:ruleset.unsafe')}
        </Badge>
      }
    >
      <div className="flex flex-col gap-4">
        {/*
         * A refused configuration is its own outcome, not a failure to terminate. Saying
         * "your rules never finish" would send an admin looking for a snake when the truth
         * is the run never happened (ADR-0004).
         */}
        {report.unsupported !== null && (
          <Alert tone="error">
            {t('admin:ruleset.engineRejection')}: {report.unsupported}
          </Alert>
        )}

        <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Figure label={t('admin:ruleset.sessions')} value={report.sessions} />
          <Figure label={t('admin:ruleset.completed')} value={report.completed} />
          <Figure label={t('admin:ruleset.exhausted')} value={report.exhausted} />
          <Figure
            label={t('admin:ruleset.entryFailureRate')}
            value={`${(report.entryFailureRate * 100).toFixed(1)}%`}
          />
        </dl>

        <section>
          <h3 className="mb-2 text-sm font-medium text-ink">{t('admin:ruleset.turns')}</h3>
          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Figure
              label={t('admin:ruleset.mean')}
              value={report.turnsToCompletion.mean.toFixed(1)}
            />
            <Figure label={t('admin:ruleset.median')} value={report.turnsToCompletion.median} />
            <Figure label={t('admin:ruleset.p95')} value={report.turnsToCompletion.p95} />
            <Figure label={t('admin:ruleset.max')} value={report.turnsToCompletion.max} />
          </dl>
        </section>

        <dl className="flex flex-wrap gap-6 text-sm">
          <Verdict label={t('admin:ruleset.terminates')} value={report.terminates} />
          <Verdict label={t('admin:ruleset.goalReachable')} value={report.goalReachable} />
          <div>
            <dt className="text-xs text-ink-muted">{t('admin:ruleset.unreachable')}</dt>
            <dd className="tabular-nums text-ink">
              {report.unreachableSquares.length === 0
                ? t('admin:ruleset.none')
                : report.unreachableSquares.join(', ')}
            </dd>
          </div>
        </dl>

        <div>
          <Button disabled={!report.safeToActivate} onClick={onActivate}>
            {t('admin:ruleset.activate')}
          </Button>
        </div>
      </div>
    </Card>
  );
}

function Figure({
  label,
  value,
}: {
  readonly label: string;
  readonly value: number | string;
}): ReactNode {
  return (
    <div className="rounded-md border border-border px-3 py-2">
      <dt className="text-xs text-ink-muted">{label}</dt>
      <dd className="mt-0.5 text-lg font-semibold tabular-nums text-ink">{value}</dd>
    </div>
  );
}

function Verdict({ label, value }: { readonly label: string; readonly value: boolean }): ReactNode {
  const { t } = useTranslation(['admin']);
  return (
    <div>
      <dt className="text-xs text-ink-muted">{label}</dt>
      <dd>
        {/* Never colour alone (WCAG 1.4.1) — the badge always carries a word. */}
        <Badge tone={value ? 'positive' : 'danger'}>
          {value ? t('admin:ruleset.yes') : t('admin:ruleset.no')}
        </Badge>
      </dd>
    </div>
  );
}
